/**
 * AdminDesktopShell (1.35.0 redesign) — адаптивный десктопный лэйаут админа.
 *
 * Брейкпойнты:
 *   • wide    (≥1280) — полный сайдбар 240px, виджеты могут идти в 3 кол.
 *   • desktop (1024-1279) — полный сайдбар 220px, виджеты в 2 кол.
 *   • tablet  (768-1023) — сайдбар скрыт за гамбургером, контент во всю.
 *                          При тапе на ☰ — выезжает overlay'ем.
 *   • mobile  (<768)   — shell не используется, fallback на мобильный
 *                        AdminPanel (старый).
 *
 * Override для разработки: ?desktop=1 в URL форсит брейкпойнт desktop
 * чтобы тестировать с узкого экрана. ?desktop=wide — форсит wide.
 * ?desktop=0 — снимает форсинг.
 */

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { C, F } from '../../../design-system/tokens';
import { ThemeToggle } from '../../../design-system/atoms/ThemeToggle';
import { CompanySwitcher } from '../../../domains/auth/CompanySwitcher';
import { useAuthStore } from '../../../domains/auth/authStore';
import { useMembershipStore, selectActiveMembership } from '../../../domains/auth/membershipStore';
import { SubscriptionIndicator } from '../../subscription/SubscriptionCenter';

// ─── Breakpoint hook ──────────────────────────────────────────────────────

export type AdminBreakpoint = 'mobile' | 'tablet' | 'desktop' | 'wide';

function readBreakpoint(): AdminBreakpoint {
  if (typeof window === 'undefined') return 'mobile';
  const w = window.innerWidth;
  if (w >= 1280) return 'wide';
  if (w >= 1024) return 'desktop';
  if (w >= 768)  return 'tablet';
  return 'mobile';
}

export function useAdminBreakpoint(): AdminBreakpoint {
  const override = useMemo<AdminBreakpoint | null>(() => {
    if (typeof window === 'undefined') return null;
    try {
      const params = new URLSearchParams(window.location.search);
      const q = params.get('desktop');
      if (q === '1') {
        window.localStorage.setItem('fs_force_desktop', 'desktop');
        return 'desktop';
      }
      if (q === 'wide') {
        window.localStorage.setItem('fs_force_desktop', 'wide');
        return 'wide';
      }
      if (q === '0') {
        window.localStorage.removeItem('fs_force_desktop');
        return null;
      }
      const stored = window.localStorage.getItem('fs_force_desktop');
      if (stored === 'wide') return 'wide';
      if (stored === 'desktop' || stored === '1') return 'desktop';
    } catch { /* localStorage может быть заблокирован */ }
    return null;
  }, []);

  const [bp, setBp] = useState<AdminBreakpoint>(() => override ?? readBreakpoint());

  useEffect(() => {
    if (override !== null) {
      setBp(override);
      return;
    }
    const handler = () => setBp(readBreakpoint());
    window.addEventListener('resize', handler);
    setBp(readBreakpoint());
    return () => window.removeEventListener('resize', handler);
  }, [override]);

  return bp;
}

export function useIsAdminDesktop(): boolean {
  const bp = useAdminBreakpoint();
  return bp !== 'mobile';
}

// ─── Context ──────────────────────────────────────────────────────────────

const AdminBreakpointContext = createContext<AdminBreakpoint>('desktop');

export function useAdminBreakpointFromContext(): AdminBreakpoint {
  return useContext(AdminBreakpointContext);
}

// ─── Структура навигации ──────────────────────────────────────────────────

interface NavLeaf {
  type: 'leaf';
  path: string;
  label: string;
  icon: string;
  exact?: boolean;
}

interface NavGroup {
  type: 'group';
  id: string;
  label: string;
  icon: string;
  children: NavLeaf[];
}

type NavEntry = NavLeaf | NavGroup;

const NAV: NavEntry[] = [
  { type: 'leaf', path: '/admin/overview', label: 'Главная', icon: '⊞', exact: true },
  { type: 'leaf', path: '/admin/order',    label: 'Заявки',  icon: '☰' },
  {
    type: 'group', id: 'team', label: 'Команда', icon: '◉',
    children: [
      { type: 'leaf', path: '/admin/users', label: 'Сотрудники', icon: '·' },
      { type: 'leaf', path: '/admin/crews', label: 'Бригады',    icon: '·' },
    ],
  },
  {
    type: 'group', id: 'catalog', label: 'Каталог', icon: '✦',
    children: [
      { type: 'leaf', path: '/admin/services',     label: 'Услуги',      icon: '·' },
      // «Типы объектов» — это шаблоны оборудования (кондиционеры, котлы и т.д.),
      // которые техник видит в карточке объекта. В обиходе это «оборудование»,
      // а не «объект» (объект = адрес/место).
      { type: 'leaf', path: '/admin/object-types', label: 'Оборудование', icon: '·' },
    ],
  },
  {
    type: 'group', id: 'finance', label: 'Финансы', icon: '₽',
    children: [
      { type: 'leaf', path: '/admin/finance',   label: 'Счета и платежи', icon: '·' },
      { type: 'leaf', path: '/admin/analytics', label: 'Аналитика',       icon: '·' },
    ],
  },
  {
    type: 'group', id: 'settings', label: 'Настройки', icon: '⚙',
    children: [
      { type: 'leaf', path: '/admin/settings',      label: 'Компания',       icon: '·' },
      { type: 'leaf', path: '/admin/billing',       label: 'Подписка',       icon: '·' },
      { type: 'leaf', path: '/admin/notifications', label: 'Уведомления',    icon: '·' },
      { type: 'leaf', path: '/admin/sync',          label: 'Синхронизация',  icon: '·' },
    ],
  },
];

// ─── Sidebar ──────────────────────────────────────────────────────────────

interface SidebarProps {
  currentPath: string;
  width: number;
  onNavigate?: () => void;
  onLogout?: () => void;
}

// Подпись роли под именем пользователя в сайдбаре.
const SIDEBAR_ROLE_LABEL: Record<string, string> = {
  company_admin: 'Администратор',
  dispatcher: 'Диспетчер',
  technician: 'Техник',
};

function Sidebar({ currentPath, width, onNavigate, onLogout }: SidebarProps) {
  const user = useAuthStore((state) => state.user);
  const roleLabel = user?.company_role
    ? (SIDEBAR_ROLE_LABEL[user.company_role] ?? user.company_role)
    : '';
  // Название компании для шапки сайдбара (бренд «Кворум» остаётся в топбаре).
  const activeMembership = useMembershipStore(selectActiveMembership);
  const companyName = activeMembership?.company_name?.trim() || 'Моя компания';
  const companyInitial = companyName.charAt(0).toUpperCase() || 'F';
  // Адаптивный кегль: длинные названия плавно уменьшаются, чтобы помещаться
  // без уродливых переносов внутри слова.
  const companyNameFontSize =
    companyName.length <= 14 ? 16 :
    companyName.length <= 22 ? 14 :
    companyName.length <= 32 ? 12.5 :
    11;
  const initialOpen = useMemo(() => {
    const open = new Set<string>();
    for (const entry of NAV) {
      if (entry.type === 'group') {
        const hit = entry.children.some((leaf) => currentPath.startsWith(leaf.path));
        if (hit) open.add(entry.id);
      }
    }
    return open;
  }, [currentPath]);

  const [openGroups, setOpenGroups] = useState<Set<string>>(initialOpen);

  useEffect(() => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      for (const entry of NAV) {
        if (entry.type === 'group') {
          const hit = entry.children.some((leaf) => currentPath.startsWith(leaf.path));
          if (hit) next.add(entry.id);
        }
      }
      return next;
    });
  }, [currentPath]);

  const toggle = (id: string) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  return (
    <aside style={{
      width,
      flexShrink: 0,
      height: '100%',
      background: `linear-gradient(180deg, ${C.surf}, ${C.bg})`,
      borderRight: `1px solid ${C.b}`,
      display: 'flex',
      flexDirection: 'column',
      overflowY: 'auto',
    }}>
      {/* Шапка сайдбара: логотип-локап + плашка плана. Придаёт премиальный
          «продуктовый» вид вместо сухой подписи раздела. */}
      <div style={{
        padding: '16px 18px 14px',
        borderBottom: `1px solid ${C.b}`,
        display: 'flex',
        alignItems: 'center',
        gap: 11,
      }}>
        <div style={{
          width: 36,
          height: 36,
          borderRadius: 10,
          background: `linear-gradient(140deg, ${C.acc}, #FF8A4C)`,
          display: 'grid',
          placeItems: 'center',
          fontFamily: F.head,
          fontWeight: 800,
          fontSize: 18,
          color: '#fff',
          letterSpacing: '-1px',
          flexShrink: 0,
          boxShadow: C.accG,
        }}>{companyInitial}</div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{
            fontFamily: F.head,
            fontWeight: 800,
            fontSize: companyNameFontSize,
            color: C.txt,
            letterSpacing: '-0.2px',
            lineHeight: 1.2,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical' as const,
            overflow: 'hidden',
            wordBreak: 'normal',
            overflowWrap: 'break-word',
            hyphens: 'manual' as const,
          }}>{companyName}</div>
          <div style={{
            fontSize: 10,
            color: C.sub,
            fontFamily: F.mono,
            fontWeight: 700,
            letterSpacing: '.4px',
            marginTop: 2,
          }}>АДМИН-ПАНЕЛЬ</div>
        </div>
      </div>

      {/* ФИО + должность пользователя — перенесено из топбара. */}
      {user && (
        <div style={{
          padding: '12px 16px',
          borderBottom: `1px solid ${C.b}`,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}>
          <div style={{
            width: 34, height: 34, borderRadius: '50%',
            background: `linear-gradient(140deg, ${C.acc}22, ${C.acc}0D)`,
            border: `1px solid ${C.acc}40`,
            display: 'grid', placeItems: 'center',
            fontFamily: F.head, fontWeight: 800, fontSize: 14, color: C.acc,
            flexShrink: 0,
          }}>
            {(user.name ?? '—').trim().charAt(0).toUpperCase() || '—'}
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{
              fontSize: 13, color: C.txt, fontFamily: F.head, fontWeight: 700,
              lineHeight: 1.2,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical' as const,
              overflow: 'hidden',
            }}>
              {user.name ?? '—'}
            </div>
            <div style={{
              fontSize: 10, color: C.sub, marginTop: 3,
              fontFamily: F.mono, letterSpacing: '.3px', textTransform: 'uppercase' as const,
            }}>
              {roleLabel}
            </div>
          </div>
        </div>
      )}

      {/* Навигация */}
      <nav style={{ flex: 1, padding: '14px 10px', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {NAV.map((entry) => {
          if (entry.type === 'leaf') {
            return <SidebarLeaf key={entry.path} leaf={entry} onNavigate={onNavigate} />;
          }
          const isOpen = openGroups.has(entry.id);
          const hasActiveChild = entry.children.some((leaf) => currentPath.startsWith(leaf.path));
          return (
            <SidebarGroup
              key={entry.id}
              group={entry}
              open={isOpen}
              hasActiveChild={hasActiveChild}
              onToggle={() => toggle(entry.id)}
              onNavigate={onNavigate}
            />
          );
        })}
      </nav>

      {/* Низ: тема и выход — друг под другом, на всю ширину (без бокового
          скролла), внизу версия. */}
      <div style={{
        borderTop: `1px solid ${C.b}`,
        padding: '12px 12px 10px',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}>
        <ThemeToggle fullWidth />
        {onLogout && (
          <button
            type="button"
            onClick={onLogout}
            style={{
              width: '100%',
              padding: '9px 12px',
              background: 'transparent',
              border: `1px solid ${C.b}`,
              borderRadius: 10,
              color: C.sub,
              fontSize: 12.5,
              fontFamily: F.head,
              fontWeight: 700,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              transition: 'background .12s, color .12s, border-color .12s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = C.re;
              e.currentTarget.style.borderColor = `${C.re}66`;
              e.currentTarget.style.background = C.reS;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = C.sub;
              e.currentTarget.style.borderColor = C.b;
              e.currentTarget.style.background = 'transparent';
            }}
          >
            <span style={{ fontSize: 14, lineHeight: 1 }}>⎋</span>
            Выйти
          </button>
        )}
        <div style={{
          fontSize: 10,
          color: C.dim,
          fontFamily: F.mono,
          letterSpacing: '.3px',
          textAlign: 'center',
          marginTop: 2,
        }}>
          v{import.meta.env.VITE_APP_VERSION ?? '?'}
        </div>
      </div>
    </aside>
  );
}

// ─── Лист сайдбара ────────────────────────────────────────────────────────

function SidebarLeaf({ leaf, onNavigate }: { leaf: NavLeaf; onNavigate?: () => void }) {
  return (
    <NavLink
      to={leaf.path}
      end={leaf.exact}
      onClick={() => onNavigate?.()}
      style={({ isActive }) => ({
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '10px 14px',
        borderRadius: 10,
        color: isActive ? C.txt : C.sub,
        background: isActive ? `linear-gradient(100deg, ${C.acc}1F, transparent)` : 'transparent',
        textDecoration: 'none',
        fontSize: 14,
        fontFamily: F.head,
        fontWeight: isActive ? 700 : 600,
        transition: 'background .12s, color .12s',
        position: 'relative',
      })}
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <span style={{
              position: 'absolute',
              left: 0,
              top: 8,
              bottom: 8,
              width: 3,
              background: C.acc,
              borderRadius: '0 3px 3px 0',
              boxShadow: `0 0 12px ${C.acc}`,
            }} />
          )}
          <span style={{
            fontSize: 15,
            width: 20,
            textAlign: 'center',
            opacity: isActive ? 1 : 0.65,
            color: isActive ? C.acc : C.sub,
            transition: 'color .12s',
          }}>
            {leaf.icon}
          </span>
          <span style={{ flex: 1 }}>{leaf.label}</span>
        </>
      )}
    </NavLink>
  );
}

// ─── Группа сайдбара ──────────────────────────────────────────────────────

interface SidebarGroupProps {
  group: NavGroup;
  open: boolean;
  hasActiveChild: boolean;
  onToggle: () => void;
  onNavigate?: () => void;
}

function SidebarGroup({ group, open, hasActiveChild, onToggle, onNavigate }: SidebarGroupProps) {
  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '10px 14px',
          borderRadius: 8,
          color: hasActiveChild ? C.txt : C.sub,
          background: 'transparent',
          border: 'none',
          textAlign: 'left',
          fontSize: 14,
          fontFamily: F.head,
          fontWeight: hasActiveChild ? 700 : 500,
          cursor: 'pointer',
          transition: 'color .12s',
        }}
        onMouseEnter={(e) => (e.currentTarget.style.color = C.txt)}
        onMouseLeave={(e) => (e.currentTarget.style.color = hasActiveChild ? C.txt : C.sub)}
      >
        <span style={{ fontSize: 15, width: 20, textAlign: 'center', opacity: 0.65 }}>
          {group.icon}
        </span>
        <span style={{ flex: 1 }}>{group.label}</span>
        <span style={{
          fontSize: 9,
          color: C.dim,
          transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
          transition: 'transform .15s',
          display: 'inline-block',
        }}>▶</span>
      </button>
      {open && (
        <div style={{
          paddingLeft: 30,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          marginTop: 2,
          marginBottom: 6,
        }}>
          {group.children.map((leaf) => (
            <NavLink
              key={leaf.path}
              to={leaf.path}
              onClick={() => onNavigate?.()}
              style={({ isActive }) => ({
                display: 'block',
                padding: '8px 14px',
                borderRadius: 6,
                color: isActive ? C.acc : C.sub,
                background: isActive ? `${C.acc}11` : 'transparent',
                textDecoration: 'none',
                fontSize: 13,
                fontFamily: F.head,
                fontWeight: isActive ? 700 : 500,
                transition: 'background .12s, color .12s',
              })}
            >
              {leaf.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── TopBar ───────────────────────────────────────────────────────────────

interface TopBarProps {
  showHamburger: boolean;
  onHamburger?: () => void;
}

function TopBar({ showHamburger, onHamburger }: TopBarProps) {
  const user = useAuthStore((state) => state.user);

  return (
    <header style={{
      height: 60,
      flexShrink: 0,
      background: C.surf,
      borderBottom: `1px solid ${C.b}`,
      display: 'flex',
      alignItems: 'center',
      gap: 14,
      padding: '0 22px',
    }}>
      {showHamburger && (
        <button
          type="button"
          onClick={onHamburger}
          aria-label="Меню"
          style={{
            width: 36, height: 36,
            background: 'transparent',
            border: `1px solid ${C.b}`,
            borderRadius: 8,
            color: C.txt,
            fontSize: 18,
            cursor: 'pointer',
            display: 'grid',
            placeItems: 'center',
            flexShrink: 0,
            transition: 'background .12s, border-color .12s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = C.cardHi;
            e.currentTarget.style.borderColor = C.bBright;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.borderColor = C.b;
          }}
        >
          ☰
        </button>
      )}

      {/* Логотип топбара — клик ведёт на главную. Всегда виден,
          не нужно открывать сайдбар чтобы вернуться. */}
      <NavLink
        to="/admin/overview"
        end
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          textDecoration: 'none',
          flexShrink: 0,
          padding: '4px 8px',
          marginLeft: -8,
          borderRadius: 8,
          transition: 'background .12s',
        }}
        onMouseEnter={(e) => (e.currentTarget.style.background = C.cardHi)}
        onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
      >
        <div style={{
          width: 28, height: 28,
          borderRadius: 7,
          background: `linear-gradient(135deg, ${C.acc}, #FF8A4C)`,
          display: 'grid',
          placeItems: 'center',
          fontFamily: F.head,
          fontWeight: 800,
          fontSize: 14,
          color: '#fff',
          letterSpacing: '-0.5px',
          flexShrink: 0,
        }}>F</div>
        <div style={{
          fontFamily: F.head,
          fontWeight: 800,
          fontSize: 15,
          color: C.txt,
          letterSpacing: '-0.3px',
        }}>Кворум</div>
      </NavLink>

      <div style={{ flex: 1, minWidth: 0 }}>
        <CompanySwitcher />
      </div>

      {user?.company_id && <SubscriptionIndicator />}
    </header>
  );
}

// ─── Drawer (мобильный выезжающий сайдбар) ────────────────────────────────

interface DrawerProps {
  open: boolean;
  onClose: () => void;
  currentPath: string;
  onLogout?: () => void;
}

function MobileDrawer({ open, onClose, currentPath, onLogout }: DrawerProps) {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      zIndex: 100,
      display: 'flex',
    }}>
      <div
        onClick={onClose}
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(2px)',
          animation: 'fs-fade-in .15s ease',
        }}
      />
      <div style={{
        position: 'relative',
        width: 280,
        maxWidth: '85vw',
        height: '100%',
        background: C.surf,
        borderRight: `1px solid ${C.b}`,
        animation: 'fs-slide-in .2s ease',
        display: 'flex',
        flexDirection: 'column',
      }}>
        <Sidebar
          currentPath={currentPath}
          width={280}
          onNavigate={onClose}
          onLogout={onLogout}
        />
      </div>
      <style>{`
        @keyframes fs-fade-in { from { opacity: 0 } to { opacity: 1 } }
        @keyframes fs-slide-in { from { transform: translateX(-100%) } to { transform: translateX(0) } }
      `}</style>
    </div>
  );
}

// ─── Главный shell ─────────────────────────────────────────────────────────

interface AdminDesktopShellProps {
  children: ReactNode;
  onLogout: () => void;
}

export function AdminDesktopShell({ children, onLogout }: AdminDesktopShellProps) {
  const location = useLocation();
  const bp = useAdminBreakpoint();

  const isNarrow = bp === 'tablet';
  const sidebarWidth =
    bp === 'wide'    ? 240 :
    bp === 'desktop' ? 220 :
    0;
  const mainPadding =
    bp === 'wide'    ? '18px 22px' :
    bp === 'desktop' ? '16px 18px' :
    bp === 'tablet'  ? '14px 14px' :
                       '12px 12px';

  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  return (
    <AdminBreakpointContext.Provider value={bp}>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100dvh',
        background: C.bg,
        overflow: 'hidden',
      }}>
        <TopBar
          showHamburger={isNarrow}
          onHamburger={() => setDrawerOpen(true)}
        />
        <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          {!isNarrow && (
            <Sidebar currentPath={location.pathname} width={sidebarWidth} onLogout={onLogout} />
          )}
          {isNarrow && (
            <MobileDrawer
              open={drawerOpen}
              onClose={() => setDrawerOpen(false)}
              currentPath={location.pathname}
              onLogout={onLogout}
            />
          )}
          <main style={{
            flex: 1,
            overflow: 'auto',
            padding: mainPadding,
          }}>
            {children}
          </main>
        </div>
      </div>
    </AdminBreakpointContext.Provider>
  );
}
